import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
} from './ui/alert-dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../lib/AuthContext';
import { supabase } from '../../lib/supabase-client';

interface DeleteAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EDGE_FUNCTION_URL = import.meta.env.VITE_SUPABASE_URL
  ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`
  : 'https://uyavxanmcwfelwqwsesr.supabase.co/functions/v1';

export function DeleteAccountDialog({ open, onOpenChange }: DeleteAccountDialogProps) {
  const { signOut } = useAuth();
  const [step, setStep] = useState<'warning' | 'confirm'>('warning');
  const [password, setPassword] = useState('');
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetState = () => {
    setStep('warning');
    setPassword('');
    setConfirmPhrase('');
    setError(null);
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      resetState();
    }
    onOpenChange(isOpen);
  };

  const handleDelete = async () => {
    if (confirmPhrase !== 'USUN KONTO') {
      setError('Wpisz "USUN KONTO" aby potwierdzic');
      return;
    }

    if (!password) {
      setError('Haslo jest wymagane');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('Sesja wygasla. Zaloguj sie ponownie.');
      }

      const response = await fetch(`${EDGE_FUNCTION_URL}/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({
          password,
          confirmPhrase,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Zbyt wiele prob. Sprobuj ponownie za godzine.');
        }
        throw new Error(data.error || 'Wystapil blad podczas usuwania konta');
      }

      // Success - sign out and redirect
      toast.success('Konto zostalo usuniete');
      await signOut();
      window.location.href = '/';

    } catch (err: unknown) {
      console.error('Delete account error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Wystapil nieoczekiwany blad';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleClose}>
      <AlertDialogContent className="max-w-md">
        {step === 'warning' ? (
          <>
            <AlertDialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <AlertDialogTitle className="text-[15px] text-red-600 dark:text-red-400">
                  Usunac konto?
                </AlertDialogTitle>
              </div>
              <AlertDialogDescription className="text-[13px] text-muted-foreground space-y-3" asChild>
                <div>
                  <p>Ta operacja jest <strong>nieodwracalna</strong>. Usuniete zostana:</p>
                  <ul className="list-disc list-inside space-y-1 text-[12px]">
                    <li>Wszystkie Twoje nieruchomosci i zdjecia</li>
                    <li>Wszystkie wygenerowane wizualizacje</li>
                    <li>Historia faktur i dane rozliczeniowe</li>
                    <li>Ustawienia konta i preferencje</li>
                    <li>Aktywna subskrypcja (zostanie anulowana)</li>
                  </ul>
                  <p className="text-[11px] text-muted-foreground/70 pt-2">
                    Nie bedzie mozliwosci odzyskania tych danych.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2 sm:gap-2 mt-4">
              <AlertDialogCancel className="text-[13px]">Anuluj</AlertDialogCancel>
              <Button
                variant="destructive"
                onClick={() => setStep('confirm')}
                className="text-[13px]"
              >
                Kontynuuj
              </Button>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[15px] text-red-600 dark:text-red-400">
                Potwierdz usuniecie konta
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[13px]">
                Aby usunac konto, wprowadz haslo i wpisz "USUN KONTO".
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="delete-password" className="text-[13px]">
                  Haslo
                </Label>
                <Input
                  id="delete-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Wprowadz haslo"
                  className="h-9 text-[13px]"
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-phrase" className="text-[13px]">
                  Wpisz "USUN KONTO" aby potwierdzic
                </Label>
                <Input
                  id="confirm-phrase"
                  type="text"
                  value={confirmPhrase}
                  onChange={(e) => setConfirmPhrase(e.target.value.toUpperCase())}
                  placeholder="USUN KONTO"
                  className="h-9 text-[13px]"
                  disabled={loading}
                />
              </div>

              {error && (
                <p className="text-[12px] text-red-600 dark:text-red-400">
                  {error}
                </p>
              )}
            </div>

            <AlertDialogFooter className="gap-2 sm:gap-2">
              <Button
                variant="outline"
                onClick={() => setStep('warning')}
                disabled={loading}
                className="text-[13px]"
              >
                Wstecz
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={loading || confirmPhrase !== 'USUN KONTO' || !password}
                className="text-[13px]"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Usuwanie...
                  </>
                ) : (
                  'Usun konto na zawsze'
                )}
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
